import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthStore } from '@/features/auth/auth-store';

import { deleteCampaign, getCampaign, listCampaigns, updateOutcome } from '../campaigns-api';

import type { CampaignDetail, CampaignOutcomeStatus, CampaignSummary } from './campaign.schema';

// ── Query keys ──────────────────────────────────────────────────────────────
const campaignKey = (id: string) => ['campaign', id] as const;

/**
 * Lists persisted campaigns for the selector (SCRUM-71). Server-backed, so campaigns stay
 * reachable across sessions (unlike the retired in-memory recent-campaign chips).
 *
 * Token-gated (`enabled: accessToken !== null`) + `retry: 2` — the mandatory pattern for any
 * authed /api/* query (GOTCHA 5fe78eb9): firing before the in-memory Bearer is set lets the
 * global `retry:false` + `staleTime:Infinity` config cache the resulting 401 for the whole
 * session. React Query auto-runs the idle query once the token arrives via the store selector.
 *
 * `queryKey: ['campaigns']` is intentionally the same key the wizard invalidates after a
 * successful persist, so sending a campaign refreshes the selector automatically.
 */
export function useCampaigns() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery<CampaignSummary[]>({
    queryKey: ['campaigns'],
    queryFn: listCampaigns,
    enabled: accessToken !== null,
    staleTime: Infinity,
    retry: 2,
  });
}

/**
 * Fetches one campaign's detail + outcome rows (SCRUM-72/73). Enabled only when a campaign is
 * selected AND the in-memory Bearer exists; the summary bar derives its counts from the same
 * `outcomes` array this returns, so the bar and the table can never diverge (plan RT3).
 *
 * The `accessToken !== null` gate is mandatory for any authed /api/* hook (GOTCHA 5fe78eb9):
 * firing before the token is set lets the global `retry:false` + `staleTime:Infinity` config
 * cache the resulting 401 for the whole session. React Query auto-runs the idle query once the
 * token arrives via the useAuthStore selector.
 */
export function useCampaign(campaignId: string | null) {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery<CampaignDetail>({
    queryKey: campaignKey(campaignId ?? '__none__'),
    // The enabled guard guarantees a non-null id at call time; the check satisfies the type
    // without a banned non-null assertion.
    queryFn: () => {
      if (campaignId === null) {
        throw new Error('useCampaign queryFn ran without a campaign id.');
      }
      return getCampaign(campaignId);
    },
    enabled: campaignId !== null && accessToken !== null,
    staleTime: Infinity,
  });
}

/**
 * Records an outcome status for one customer (SCRUM-74), optimistically updating the cached
 * campaign detail so the row badge AND the derived summary bar reflect the change instantly.
 * On failure the snapshot is restored (the UI must never show a status the server rejected —
 * plan RT2/SC6); on settle the campaign is re-fetched to reconcile with the authoritative
 * `updatedAt`.
 */
export function useUpdateOutcome(campaignId: string) {
  const queryClient = useQueryClient();
  const key = campaignKey(campaignId);

  return useMutation<
    undefined,
    Error,
    { customerId: string; status: CampaignOutcomeStatus },
    { previous: CampaignDetail | undefined }
  >({
    mutationFn: async ({ customerId, status }) => {
      await updateOutcome(campaignId, customerId, status);
      return undefined;
    },
    onMutate: async ({ customerId, status }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CampaignDetail>(key);
      if (previous) {
        queryClient.setQueryData<CampaignDetail>(key, {
          ...previous,
          outcomes: previous.outcomes.map((o) =>
            o.customerId === customerId ? { ...o, status } : o,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData<CampaignDetail>(key, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * Permanently deletes a campaign (SCRUM-813). On success it invalidates the selector list
 * (`['campaigns']`) so the deleted campaign disappears from the chips, and removes the now-stale
 * detail entry (`['campaign', id]`) from the cache so nothing refetches a 404. The caller is
 * responsible for navigating away from the (now-gone) detail view — see CampaignDetailView's
 * onBack() on success. Bank-Executive-only is enforced server-side; a non-Executive caller who
 * reached this (parity bug) gets a rejected promise the dialog surfaces as an error.
 */
export function useDeleteCampaign(campaignId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<void> => {
      await deleteCampaign(campaignId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.removeQueries({ queryKey: campaignKey(campaignId) });
    },
  });
}
